import InlineScript from '../components/InlineScript';
import { FormEvent, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { pageUrl } from '../config/site';
import { LOGO_ALT, LOGO_SRC } from '../constants/branding';
import { BEFORE_AFTER, WORK_SHOTS } from '../constants/projectGallery';
import Navigation from '../components/navigation';
import Footer from '../components/footer';
import { submitToMailer } from '../lib/submitToMailer';
import './home.css';

type MailUi = {
  status: 'idle' | 'sending' | 'ok' | 'err';
  message: string;
};

export default function Home() {
  const [ctaMail, setCtaMail] = useState<MailUi>({
    status: 'idle',
    message: '',
  });
  const [modalMail, setModalMail] = useState<MailUi>({
    status: 'idle',
    message: '',
  });

  async function handleHomeCtaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setCtaMail({ status: 'sending', message: '' });
    const form = e.currentTarget;
    const fd = new FormData(form);
    const result = await submitToMailer({
      form: 'contact',
      name: String(fd.get('name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      service: String(fd.get('service') ?? ''),
      message: String(fd.get('message') ?? ''),
      contactTime: 'anytime',
      freeEstimate: false,
      fileCount: 0,
    });
    if (result.ok) {
      setCtaMail({
        status: 'ok',
        message: 'Thanks — we received your request and will reply soon.',
      });
      form.reset();
    } else {
      setCtaMail({ status: 'err', message: result.error });
    }
  }

  async function handleHomeModalSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setModalMail({ status: 'sending', message: '' });
    const form = e.currentTarget;
    const fd = new FormData(form);
    const result = await submitToMailer({
      form: 'contact',
      name: String(fd.get('name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      service: String(fd.get('service') ?? ''),
      message: String(fd.get('message') ?? ''),
      contactTime: 'anytime',
      freeEstimate: false,
      fileCount: 0,
    });
    if (result.ok) {
      setModalMail({
        status: 'ok',
        message: 'Thanks — we received your request and will reply soon.',
      });
      form.reset();
      const dlg = document.getElementById('quoteDialog') as HTMLDialogElement | null;
      dlg?.close();
    } else {
      setModalMail({ status: 'err', message: result.error });
    }
  }

  return (
    <div className="home-container1">
      <Helmet>
        <title>Shiny Touch Painting &amp; Renovation Ltd</title>
        <meta
          property="og:title"
          content="Shiny Touch Painting &amp; Renovation Ltd"
        />
        <link rel="canonical" href={pageUrl('/')} />
        <meta property="og:url" content={pageUrl('/')} />
      </Helmet>
      <Navigation></Navigation>
      <section className="hero-brand-section">
        <div className="hero-media-bg">
          <img
            src="https://images.pexels.com/photos/6474346/pexels-photo-6474346.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1500"
            alt="Professional painting interior"
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content-card">
            <div className="hero-logo-wrapper">
              <img
                src={LOGO_SRC}
                alt={LOGO_ALT}
                className="hero-brand-logo"
                width={120}
                height={120}
              />
            </div>
            <h1 className="home-hero-title hero-title">
              Shiny Touch Painting &amp; Renovation Ltd
            </h1>
            <p className="home-hero-subtitle hero-subtitle">
              Reliable. Affordable. Professional.
            </p>
            <div className="hero-location-tag">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>Burnaby, BC</span>
            </div>
            <div className="hero-cta-group">
              <button id="heroRequestBtn" className="btn btn-primary btn-lg">
                Request a Free Estimate
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="services-snapshot-section">
        <div className="services-container">
          <div className="services-header">
            <h2 className="section-title">Our Core Services</h2>
            <p className="section-subtitle">
              Expert solutions for residential and commercial properties
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 21 1.9-1.9a2.44 2.44 0 0 0 0-3.4l-1.9-1.9"></path>
                  <path d="M11 11 3 3"></path>
                  <path d="M11 11 3 19"></path>
                  <path d="M11 11 19 3"></path>
                  <path d="M11 11 19 19"></path>
                </svg>
              </div>
              <h3 className="service-name">Interior &amp; Exterior Painting</h3>
              <p className="section-content">
                Transforming spaces with precision coatings and premium finishes
                that last.
              </p>
              <a href="#">
                <div className="btn btn-link">
                  <span>View Details</span>
                </div>
              </a>
            </div>
            <div className="service-card">
              <div className="service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <h3 className="service-name">Full Renovation</h3>
              <p className="section-content">
                Complete home transformations from concept to completion with
                expert craftsmanship.
              </p>
              <a href="#">
                <div className="btn btn-link">
                  <span>View Details</span>
                </div>
              </a>
            </div>
            <div className="service-card">
              <div className="service-icon-box">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3 className="service-name">Strata Property Maintenance</h3>
              <p className="section-content">
                Reliable maintenance programs designed for property managers and
                strata councils.
              </p>
              <a href="#">
                <div className="btn btn-link">
                  <span>View Details</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="portfolio-carousel-section">
        <div className="portfolio-header">
          <h2 className="section-title">Before &amp; After Results</h2>
          <p className="section-subtitle">
            Real results for our valued clients in Burnaby
          </p>
        </div>
        <div className="portfolio-carousel-wrapper">
          <div id="portfolioTrack" className="portfolio-track">
            <div className="portfolio-item">
              <div className="comparison-container">
                <div className="comparison-side">
                  <img
                    src={BEFORE_AFTER.project0.before}
                    alt="Before — project one"
                    className="comparison-img"
                  />
                  <span className="comparison-label">Before</span>
                </div>
                <div className="comparison-side">
                  <img
                    src={BEFORE_AFTER.project0.after}
                    alt="After — project one"
                    className="comparison-img"
                  />
                  <span className="comparison-label">After</span>
                </div>
              </div>
            </div>
            <div className="portfolio-item">
              <div className="comparison-container">
                <div className="comparison-side">
                  <img
                    src={BEFORE_AFTER.project1.before}
                    alt="Before — project two"
                    className="comparison-img"
                  />
                  <span className="comparison-label">Before</span>
                </div>
                <div className="comparison-side">
                  <img
                    src={BEFORE_AFTER.project1.after}
                    alt="After — project two"
                    className="comparison-img"
                  />
                  <span className="comparison-label">After</span>
                </div>
              </div>
            </div>
            <div className="portfolio-item">
              <div className="comparison-container">
                <div className="comparison-side">
                  <img
                    src={WORK_SHOTS[0]}
                    alt="Work in progress on site"
                    className="comparison-img"
                  />
                  <span className="comparison-label">In progress</span>
                </div>
                <div className="comparison-side">
                  <img
                    src={WORK_SHOTS[2]}
                    alt="Finished workmanship"
                    className="comparison-img"
                  />
                  <span className="comparison-label">Finished</span>
                </div>
              </div>
            </div>
          </div>
          <div className="carousel-nav">
            <button id="prevBtn" className="carousel-btn prev">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button id="nextBtn" className="carousel-btn next">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
        <div className="portfolio-footer">
          <Link to="/before-after-gallery">
            <div className="btn btn-outline">
              <span>View Full Before &amp; After Gallery</span>
            </div>
          </Link>
        </div>
      </section>
      <section className="trust-signals-section">
        <div className="trust-container">
          <div className="trust-header">
            <h2 className="section-title">Why Choose Us</h2>
            <p className="section-subtitle">
              The Shiny Touch advantage for your peace of mind
            </p>
          </div>
          <div className="trust-grid">
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Free Estimates</h4>
                <p className="section-content">
                  No-obligation quotes for all our services.
                </p>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Licensed &amp; Insured</h4>
                <p className="section-content">
                  Full liability coverage for every project.
                </p>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <path d="m9 16 2 2 4-4"></path>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Reliable Scheduling</h4>
                <p className="section-content">
                  We respect your time and stay on track.
                </p>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Competitive Pricing</h4>
                <p className="section-content">
                  Premium quality that fits your budget.
                </p>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                  <path d="M4 22h16"></path>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Years of Experience</h4>
                <p className="section-content">
                  Decades of collective renovation expertise.
                </p>
              </div>
            </div>
            <div className="trust-item">
              <div className="trust-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <polyline points="16 11 18 13 22 9"></polyline>
                </svg>
              </div>
              <div className="trust-text">
                <h4 className="trust-title">Expert Team</h4>
                <p className="section-content">
                  Certified professionals on every job site.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="testimonials-carousel-section">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Trusted by homeowners and strata managers across BC
            </p>
          </div>
          <div className="testimonials-wrapper">
            <div id="testimonialTrack" className="testimonials-track">
              <div className="testimonial-item">
                <div className="home-testimonial-card">
                  <div className="home-quote-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V3H21.017V15C21.017 18.3137 18.3307 21 15.017 21H14.017ZM3.017 21L3.017 18C3.017 16.8954 3.91243 16 5.017 16H8.017C8.56928 16 9.017 15.5523 9.017 15V9C9.017 8.44772 8.56928 8 8.017 8H5.017C3.91243 8 3.017 7.10457 3.017 6V3H10.017V15C10.017 18.3137 7.33072 21 4.017 21H3.017Z"></path>
                    </svg>
                  </div>
                  <p className="testimonial-text">
                    &quot;Shiny Touch was incredibly punctual and professional.
                    Their workmanship on our full home renovation was
                    outstanding. Highly recommend for any strata projects!&quot;
                  </p>
                  <div className="home-testimonial-author">
                    <span className="author-name">Sarah Jenkins</span>
                    <span className="author-role">Homeowner, Burnaby</span>
                  </div>
                </div>
              </div>
              <div className="testimonial-item">
                <div className="home-testimonial-card">
                  <div className="home-quote-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C14.9124 8 14.017 7.10457 14.017 6V3H21.017V15C21.017 18.3137 18.3307 21 15.017 21H14.017ZM3.017 21L3.017 18C3.017 16.8954 3.91243 16 5.017 16H8.017C8.56928 16 9.017 15.5523 9.017 15V9C9.017 8.44772 8.56928 8 8.017 8H5.017C3.91243 8 3.017 7.10457 3.017 6V3H10.017V15C10.017 18.3137 7.33072 21 4.017 21H3.017Z"></path>
                    </svg>
                  </div>
                  <p className="testimonial-text">
                    &quot;We&apos;ve used them for multiple strata property
                    maintenance jobs. Their reliability and attention to detail
                    are why they are our first choice for painting.&quot;
                  </p>
                  <div className="home-testimonial-author">
                    <span className="author-name">Michael Chen</span>
                    <span className="author-role">Strata Manager</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="testimonials-nav">
              <button
                data-index="0"
                aria-label="Slide 1"
                className="nav-dot active"
              ></button>
              <button
                data-index="1"
                aria-label="Slide 2"
                className="nav-dot"
              ></button>
            </div>
          </div>
        </div>
      </section>
      <section className="showcase-highlight-section">
        <div className="showcase-split">
          <div className="showcase-media-side">
            <div className="showcase-comparison">
              <div className="comparison-layer before">
                <img
                  src={BEFORE_AFTER.project0.before}
                  alt="Before — featured transformation"
                  className="showcase-img"
                />
                <span className="showcase-tag">Before</span>
              </div>
              <div className="comparison-layer after">
                <img
                  src={BEFORE_AFTER.project0.after}
                  alt="After — featured transformation"
                  className="showcase-img"
                />
                <span className="showcase-tag">After</span>
              </div>
            </div>
          </div>
          <div className="showcase-content-side">
            <div className="showcase-info">
              <h2 className="section-title">Modern Apartment Transformation</h2>
              <p className="section-content">
                A comprehensive renovation project in a Burnaby residence,
                focusing on spatial optimization and premium finishes.
              </p>
              <div className="showcase-details">
                <div className="detail-item">
                  <strong>Scope:</strong>
                  <span>
                    Full cabinet refinishing, backsplash installation, and
                    designer painting.
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Results:</strong>
                  <span>
                    Increased property value by 15% and achieved a modern,
                    open-concept feel.
                  </span>
                </div>
              </div>
              <button className="btn btn-primary">
                Learn About Our Process
              </button>
            </div>
          </div>
        </div>
      </section>
      <section className="estimate-cta-section">
        <div className="cta-split-container">
          <div className="cta-text-side">
            <h2 className="section-title">Ready for a Fresh Start?</h2>
            <p className="section-content">
              Request a Free Quote today. We provide reliable and affordable
              services for your home or business.
            </p>
            <div className="cta-contact-info">
              <div className="contact-row">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <a href="tel:+16045550199">
                  <div className="contact-link">
                    <span>Call for Immediate Service</span>
                  </div>
                </a>
              </div>
              <div className="contact-row">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>99632 Market Crossing PO, Burnaby, BC V5J 0H7</span>
              </div>
            </div>
          </div>
          <div className="cta-form-side">
            <div className="cta-form-card">
              <form
                data-form-id="a4ed92ac-a6d0-41f9-892d-a3a7fcf4eb16"
                className="quote-form"
                onSubmit={handleHomeCtaSubmit}
              >
                <div className="home-form-group">
                  <input
                    type="text"
                    id="home-cta-name"
                    name="name"
                    placeholder="Your Name"
                    required
                    data-form-field-id="name"
                    className="home-form-input"
                  />
                </div>
                <div className="home-form-group">
                  <input
                    type="tel"
                    id="home-cta-phone"
                    name="phone"
                    placeholder="Phone Number"
                    required
                    data-form-field-id="phone"
                    className="home-form-input"
                  />
                </div>
                <div className="home-form-group">
                  <input
                    type="email"
                    id="home-cta-email"
                    name="email"
                    placeholder="Email Address"
                    required
                    data-form-field-id="email"
                    className="home-form-input"
                  />
                </div>
                <div className="home-form-group">
                  <select
                    id="home-cta-service"
                    name="service"
                    required
                    defaultValue=""
                    data-form-field-id="service"
                    className="home-form-input"
                  >
                    <option value="" disabled>
                      Select Service
                    </option>
                    <option value="interior-painting">Interior Painting</option>
                    <option value="exterior-painting">Exterior Painting</option>
                    <option value="full-renovation">Full Renovation</option>
                    <option value="strata-maintenance">Strata Maintenance</option>
                  </select>
                </div>
                <div className="home-form-group">
                  <textarea
                    id="home-cta-message"
                    name="message"
                    required
                    rows={3}
                    placeholder="Brief project details…"
                    data-form-field-id="message"
                    className="home-form-input"
                  />
                </div>
                {ctaMail.message ? (
                  <p
                    className="section-content"
                    style={{
                      marginBottom: '0.75rem',
                      fontSize: '0.9rem',
                      color:
                        ctaMail.status === 'ok'
                          ? 'var(--color-primary)'
                          : 'var(--color-accent)',
                    }}
                    role="status"
                  >
                    {ctaMail.message}
                  </p>
                ) : null}
                <button
                  type="submit"
                  id="home-cta-submit"
                  name="button"
                  data-form-field-id="home-cta-submit"
                  className="btn-block btn btn-lg btn-accent"
                  disabled={ctaMail.status === 'sending'}
                >
                  {ctaMail.status === 'sending' ? 'Sending…' : 'Get My Free Quote'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <dialog id="quoteDialog" className="quote-modal">
        <div className="modal-header">
          <h3 className="section-title">Request a Free Estimate</h3>
          <button id="closeQuoteBtn" className="modal-close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <form
            data-form-id="42da2736-9bef-4b5b-b33f-4ef20c3ce4c5"
            className="modal-form"
            onSubmit={handleHomeModalSubmit}
          >
            <div className="form-row">
              <input
                type="text"
                placeholder="Full Name"
                required
                id="home-modal-name"
                name="name"
                data-form-field-id="home-modal-name"
                className="home-form-input"
              />
              <input
                type="tel"
                placeholder="Phone Number"
                required
                id="home-modal-phone"
                name="phone"
                data-form-field-id="home-modal-phone"
                className="home-form-input"
              />
            </div>
            <input
              type="email"
              placeholder="Email Address"
              required
              id="home-modal-email"
              name="email"
              data-form-field-id="home-modal-email"
              className="home-form-input"
            />
            <select
              id="home-modal-service"
              name="service"
              required
              defaultValue=""
              data-form-field-id="home-modal-service"
              className="home-form-input"
              style={{ marginBottom: '0.75rem' }}
            >
              <option value="" disabled>
                Select Service
              </option>
              <option value="interior-painting">Interior Painting</option>
              <option value="exterior-painting">Exterior Painting</option>
              <option value="full-renovation">Full Renovation</option>
              <option value="strata-maintenance">Strata Maintenance</option>
            </select>
            <textarea
              placeholder="Tell us about your project..."
              required
              rows={4}
              id="home-modal-message"
              name="message"
              data-form-field-id="home-modal-message"
              className="home-form-input"
            ></textarea>
            {modalMail.message ? (
              <p
                className="section-content"
                style={{
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem',
                  color:
                    modalMail.status === 'ok'
                      ? 'var(--color-primary)'
                      : 'var(--color-accent)',
                }}
                role="status"
              >
                {modalMail.message}
              </p>
            ) : null}
            <button
              type="submit"
              id="home-modal-submit"
              name="button"
              data-form-field-id="home-modal-submit"
              className="btn btn-primary btn-lg"
              disabled={modalMail.status === 'sending'}
            >
              {modalMail.status === 'sending' ? 'Sending…' : 'Send Request'}
            </button>
          </form>
        </div>
      </dialog>
      <div className="home-container2">
        <div className="home-container3">
          <InlineScript
            html={`<script defer data-name="shiny-touch-logic">
(function(){
  // Portfolio Carousel Logic
  const portfolioTrack = document.getElementById("portfolioTrack")
  const prevBtn = document.getElementById("prevBtn")
  const nextBtn = document.getElementById("nextBtn")
  let portfolioIndex = 0

  function updatePortfolio() {
    portfolioTrack.style.transform = \`translateX(-\${portfolioIndex * 100}%)\`
  }

  nextBtn.addEventListener("click", () => {
    portfolioIndex = (portfolioIndex + 1) % 3
    updatePortfolio()
  })

  prevBtn.addEventListener("click", () => {
    portfolioIndex = (portfolioIndex - 1 + 3) % 3
    updatePortfolio()
  })

  // Testimonials Carousel Logic
  const testimonialTrack = document.getElementById("testimonialTrack")
  const dots = document.querySelectorAll(".nav-dot")

  dots.forEach((dot) => {
    dot.addEventListener("click", (e) => {
      const index = parseInt(e.target.dataset.index)
      testimonialTrack.style.transform = \`translateX(-\${index * 100}%)\`
      dots.forEach((d) => d.classList.remove("active"))
      e.target.classList.add("active")
    })
  })

  // Modal Logic
  const quoteModal = document.getElementById("quoteDialog")
  const heroBtn = document.getElementById("heroRequestBtn")
  const closeBtn = document.getElementById("closeQuoteBtn")

  heroBtn.addEventListener("click", () => {
    quoteModal.showModal()
  })

  closeBtn.addEventListener("click", () => {
    quoteModal.close()
  })

  quoteModal.addEventListener("click", (e) => {
    if (e.target === quoteModal) {
      quoteModal.close()
    }
  })

  // Form Submission Enhancement
  const forms = document.querySelectorAll("form")
  forms.forEach((form) => {
    form.addEventListener("submit", (e) => {
      // In a real scenario, this would let native submission happen or handle with AJAX
      // For this demo, we'll show a simple success feedback if it were prevented
      // e.preventDefault();
      // alert('Thank you for your request! We will contact you shortly.');
    })
  })
})()
</script>`}
          ></InlineScript>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
}
